import type { PackageTypeUiOption } from '../../core/api/product-master/mappers/map-package-selection-to-ui';
import type { PackageSelectionConstraints } from '../../core/api/product-master/models/package-selection.model';

/**
 * Product Master `packageTypeCode` used as the Package step form selected id.
 * `'1'` Envelope | `'2'` Pack | `'3'` Customer | `'4'` Box
 */
export type PackageTypeId = '1' | '2' | '3' | '4';

/** Re-export UI option type for Package step consumers. */
export type PackageTypeOption = PackageTypeUiOption;

/** Product Master packageTypeCode for Purolator Express Envelope. */
export const EXPRESS_ENVELOPE_TYPE_CODE: PackageTypeId = '1';

/** Product Master packageTypeCode for Purolator Express Pack. */
export const EXPRESS_PACK_TYPE_CODE: PackageTypeId = '2';

/** Product Master packageTypeCode for customer packaging. */
export const CUSTOMER_PACKAGING_TYPE_CODE: PackageTypeId = '3';

/** Product Master packageTypeCode for Purolator Express Box. */
export const EXPRESS_BOX_TYPE_CODE: PackageTypeId = '4';

/** Maximum tracking references allowed on the Package step. */
export const MAX_TRACKING_REFERENCES = 1;

/** Maximum characters allowed per tracking reference field. */
export const TRACKING_REFERENCE_MAX_LENGTH = 35;

/** Maximum characters allowed for delivery instructions (label Notes field limit). */
export const DELIVERY_INSTRUCTIONS_MAX_LENGTH = 50;

/** Measurement unit pairs for the customer packaging toggle. */
export type PackageUnitSystem = 'imperial' | 'metric';

/**
 * Returns whether the package type code is customer packaging (requires dimensions).
 */
export function isCustomerPackaging(packageTypeCode: string | null | undefined): boolean {
  return packageTypeCode === CUSTOMER_PACKAGING_TYPE_CODE;
}

/**
 * Returns whether the package type code is Purolator Express Envelope.
 */
export function isExpressEnvelope(packageTypeCode: string | null | undefined): boolean {
  return packageTypeCode === EXPRESS_ENVELOPE_TYPE_CODE;
}

/**
 * Returns whether the package type code is Purolator Express Pack.
 */
export function isExpressPack(packageTypeCode: string | null | undefined): boolean {
  return packageTypeCode === EXPRESS_PACK_TYPE_CODE;
}

/**
 * Returns whether the package type code is Purolator Express Box.
 */
export function isExpressBox(packageTypeCode: string | null | undefined): boolean {
  return packageTypeCode === EXPRESS_BOX_TYPE_CODE;
}

/**
 * Returns whether the package type is Express Pack or Express Box (weight entry).
 */
export function isExpressPackOrBox(packageTypeCode: string | null | undefined): boolean {
  return isExpressPack(packageTypeCode) || isExpressBox(packageTypeCode);
}

/**
 * Returns whether the package is an Express catalogue type (Envelope, Pack, or Box)
 * that rates without Customer Packaging piece-row dimensions.
 */
export function isExpressCataloguePackage(packageTypeCode: string | null | undefined): boolean {
  return isExpressEnvelope(packageTypeCode) || isExpressPackOrBox(packageTypeCode);
}

/**
 * Returns whether the package is Express single-piece (`quantity.equal === 1`).
 */
export function isSinglePiecePackage(
  constraints: PackageSelectionConstraints | null | undefined,
): boolean {
  return constraints?.quantity.equal === 1;
}
